import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import Card from 'components/inputs/Card';
import { MdClose } from 'react-icons/md';
import { Row, Col } from 'react-bootstrap';
import { Formik, Form, FieldArray, getIn } from 'formik';
import TextInput from 'components/inputs/Input';
import Button from 'components/inputs/Button';
import { useDispatch, useSelector } from 'react-redux';
// import { CREATE_TARIFF, UPDATE_TARIFF } from 'actions/tariff';
import _ from 'lodash';
import * as Yup from 'yup';
import Select from 'components/inputs/Select';
import { BiPlus, BiTrash } from 'react-icons/bi';
import { CREATE_STATUS_CODE, DEFAULT_LIMIT, UPDATE_STATUS_CODE } from 'components/common/constant';
import { FETCH_USER_GROUP } from 'actions/userGroup';
import { useTranslation } from 'react-i18next';
// import { toast } from 'react-toastify';
import { GET_STATION_BY_CITY } from 'actions/chargingStation';
import { FETCH_STATE, GET_CITY_BY_STATE } from 'actions/address';
import moment from 'moment';
import { CREATE_USER_GROUP_PRICING, UPDATE_USER_GROUP_PRICING } from 'actions/userGroupPricing';
import { getCityByState as getCityByStateAPI } from 'api/address';

const PricingForm = (props) => {
  const { onClose = _.noop(), isEdit, onSuccess = _.noop() } = props;
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const validateTimeRange = (from, to) => {
    if (!from || !to) return true;
    const fromTime = moment(from, 'HH:mm');
    const toTime = moment(to, 'HH:mm');
    return toTime.isAfter(fromTime);
  };

  const userGroupPricingDetail = useSelector((state) => state.userGroupPricing.userGroupPricingDetail);
  const allUserGroup = useSelector((state) => state.userGroup.userGroups);
  const allState = useSelector((state) => state.state.states);
  // const allCity = useSelector((state) => state.city.city);
  const cityByState = useSelector((state) => state.cityByState.cityByStates);
  const statePage = useSelector((state) => state.state.page);
  const stateTotalPage = useSelector((state) => state.state.totalPages);
  const allStationByCity = useSelector((state) => state.stationByCity.stationByCities);
  const profileData = useSelector((state) => state.profile.userProfile);
  const userProfileStations = profileData.charging_stations;
  const cityPage = useSelector((state) => state.city.page);
  const cityTotalPage = useSelector((state) => state.city.totalPages);

  const getCityByState = useCallback((data = {}) => {
      dispatch({ type: GET_CITY_BY_STATE.REQUEST, payload: data });
  }, []);

  const getAllCitiesByState = useCallback(async (stateNames) => {
    if (!stateNames || (Array.isArray(stateNames) && stateNames.length === 0)) {
      return;
    }

    try {
      const selectedStateNames = Array.isArray(stateNames)
        ? stateNames.map(state => typeof state === 'string' ? state : state.value || state)
        : [stateNames];

      // Fetch first page
      const firstPageResponse = await getCityByStateAPI({ state_names: selectedStateNames, page: 1 });
      const firstPageData = firstPageResponse.data;
      let allResults = [...firstPageData.results];

      // If there are more pages, fetch all remaining pages
      if (firstPageData.totalPages > 1) {
        for (let page = 2; page <= firstPageData.totalPages; page++) {
          const pageResponse = await getCityByStateAPI({ state_names: selectedStateNames, page });
          if (pageResponse.data && pageResponse.data.results) {
            allResults = [...allResults, ...pageResponse.data.results];
          }
        }
      }

      // Dispatch success with all results combined
      dispatch({
        type: GET_CITY_BY_STATE.SUCCESS,
        payload: {
          ...firstPageData,
          results: allResults,
          page: 1,
          totalPages: 1, // Set to 1 since we've loaded all pages
        },
      });
    } catch (error) {
      dispatch({ type: GET_CITY_BY_STATE.FAIL, payload: { error } });
    }
  }, [dispatch]);

  const addTariff = useCallback((data) => {
    dispatch({
      type: CREATE_USER_GROUP_PRICING.REQUEST,
      payload: data,
      cb: (res) => {
        if (_.get(res, 'status') === CREATE_STATUS_CODE) {
          onSuccess();
          onClose();
        }
      },
    });
  }, []);

  const updateTariff = useCallback((data) => {
    dispatch({
      type: UPDATE_USER_GROUP_PRICING.REQUEST,
      payload: data,
      cb: (res) => {
        if (_.get(res, 'status') === UPDATE_STATUS_CODE) {
          onSuccess();
          onClose();
        }
      },
    });
  }, []);

  const getAllUserGroup = useCallback((data = {}) => {
    dispatch({ type: FETCH_USER_GROUP.REQUEST, payload: data });
  }, []);

  const getAllStation = useCallback((data = {}, filterTenant = true) => {
    const stationData = {
      ...data,
      filterTenant,
    };
    dispatch({ type: GET_STATION_BY_CITY.REQUEST, payload: stationData });
  }, []);

  const getStateByCountry = useCallback((data = {}) => {
    dispatch({ type: FETCH_STATE.REQUEST, payload: { ...data, deleted: true } });
  }, []);

  useEffect(() => {
    getAllUserGroup({ limit: DEFAULT_LIMIT, is_data_exists: true });
    getStateByCountry({ limit: DEFAULT_LIMIT });

    if (isEdit && userGroupPricingDetail?.states?.length > 0) {
      getAllStation({ state_array: userGroupPricingDetail.states }, false);
    } else {
      getAllStation({}, false);
    }
  }, [isEdit, userGroupPricingDetail]);

  const initialValues = isEdit
    ? {
        ...userGroupPricingDetail,
        user_group: userGroupPricingDetail.user_group ? userGroupPricingDetail.user_group.id : '',
        charging_stations: userGroupPricingDetail.charging_stations
          ? _.map(_.get(userGroupPricingDetail, 'charging_stations'), (station) => station.id)
          : [],
        chargers: userGroupPricingDetail.chargers ? _.map(_.get(userGroupPricingDetail, 'chargers'), (charger) => charger.id) : [],
      }
    : {
        user_group: '',
        states: [],
        city:[],
        charging_stations: [],
        chargers: [],
        schedule: [
          {
            from: moment().startOf('day').format('HH:mm'),
            to: moment().endOf('day').format('HH:mm'),
            price_per_unit: '',
          },
        ],
      };

  const userGroupPricingSchema = Yup.object().shape({
    user_group: Yup.string().required(t('groupPricingForm.userGroup')),
    schedule: Yup.array()
      .of(
        Yup.object().shape({
          price_per_unit: Yup.number()
            .transform((value, originalValue) => (originalValue === '' || originalValue == null ? undefined : value))
            .typeError(t('groupPricingForm.rate'))
            .required(t('groupPricingForm.rate')),
          from: Yup.string().required(t('groupPricingForm.fromTo')),
          to: Yup.string()
            .required(t('groupPricingForm.fromTo'))
            .test('is-greater', t('groupPricingForm.toMustBeAfterFrom'), function (to) {
              return validateTimeRange(this.parent.from, to);
            }),
        })
      )
      .test('time-validations', null, function (schedules) {
        if (!schedules) return true;

        const toMinutes = (time) => {
          if (!time) return null;
          const [h, m] = time.split(':').map(Number);
          return h * 60 + m;
        };

        let errors = [];

        for (let i = 0; i < schedules.length; i++) {
          const aFrom = toMinutes(schedules[i]?.from);
          const aTo = toMinutes(schedules[i]?.to);
          if (aFrom == null || aTo == null) continue;

          // 🔴 Case 1: same row check
          if (aFrom >= aTo) {
            errors.push(
              this.createError({
                path: `schedule[${i}].from`,
                message: 'From time must be less than To time',
              })
            );
          }

          // 🔴 Case 2: compare with next rows
          for (let j = i + 1; j < schedules.length; j++) {
            const bFrom = toMinutes(schedules[j]?.from);
            const bTo = toMinutes(schedules[j]?.to);
            if (bFrom == null || bTo == null) continue;

            // Normal overlap
            if (aFrom < bTo && bFrom < aTo) {
              errors.push(this.createError({ path: `schedule[${i}].from`, message: 'Time is overlapping' }));
              errors.push(this.createError({ path: `schedule[${j}].from`, message: 'Time is overlapping' }));
            }

            // 🔴 Case 3: strict next slot rule
            if (aTo === bFrom) {
              errors.push(
                this.createError({
                  path: `schedule[${j}].from`,
                  message: 'Next slot must start after previous slot',
                })
              );
            }
          }
        }

        if (errors.length > 0) {
          throw new Yup.ValidationError(errors);
        }

        return true;
      }),
  });

  return (
    <React.Fragment>
      <div className="pricing-form-page__main">
        <Card>
          <div className="pricing-form-header__block">
            <div className="pricing-header-name">
              <span>{`${isEdit ? 'Edit' : 'Add New'} User Group Pricing`}</span>
            </div>
            <div className="close-btn">
              <MdClose size={30} color="#be210b" onClick={onClose} />
            </div>
          </div>
          <div className="pricing-form-body__block">
            <div className="pricing-form--block">
              <Formik
                enableReinitialize={!!isEdit}
                initialValues={initialValues}
                validationSchema={userGroupPricingSchema}
                onSubmit={(values, { setSubmitting }) => {
                  const cleanedValues = { ...values };

                  if (_.isEmpty(cleanedValues.states)) {
                    delete cleanedValues.states;
                  }

                  if (_.isEmpty(cleanedValues.charging_stations)) {
                    delete cleanedValues.charging_stations;
                  }

                  if (_.isEmpty(cleanedValues.chargers)) {
                    delete cleanedValues.chargers;
                  }

                  // deleted city right now not need in backend
                  delete cleanedValues.city;
                  
                  const addTariffData = {
                    ...cleanedValues,
                  };
                  if (isEdit) {
                    const newTariff = {
                      ...addTariffData,
                    };
                    const editTariffData = _.omit(newTariff, ['deleted', 'createdAt', 'updatedAt', 'deletedAt']);
                    updateTariff(editTariffData);
                  } else {
                    addTariff(addTariffData);
                  }

                  setSubmitting(false);
                }}
              >
                {({ values, handleSubmit, errors, touched, setFieldValue }) => (
                  <Form onSubmit={handleSubmit}>
                    <Row>
                      {(() => {
                        const dropdownOptions = _.map(allUserGroup, (item) => ({
                          label: item.name,
                          value: item.id,
                        }));

                        const selectedGroup = userGroupPricingDetail?.user_group;

                        if (isEdit && selectedGroup && !dropdownOptions.find((opt) => opt.value === selectedGroup.id)) {
                          dropdownOptions.push({
                            label: selectedGroup.name,
                            value: selectedGroup.id,
                          });
                        }

                        return (
                          <Col lg={6} md={12}>
                            <Select
                              isRequired
                              label={t('groupPricingForm.userGroup')}
                              options={dropdownOptions}
                              placeholder={t('groupPricingForm.userGroup')}
                              name="user_group"
                              value={values.user_group}
                              onChange={(val) => setFieldValue(`user_group`, val)}
                              error={errors.user_group && touched.user_group ? errors.user_group : null}
                            />
                          </Col>
                        );
                      })()}
                      <Col lg={3} md={6}>
                        <Select
                          label={t('filters.state')}
                          options={[
                            ..._.map(allState, (state) => {
                              return { label: state.name, value: state.name };
                            }),
                          ]}
                          className="filter-select-box"
                          placeholder={t('placeHolder.selectState')}
                          isMulti
                          name="states"
                          value={values.states}
                          onMenuScrollDown={true}
                          page={statePage}
                          totalPage={stateTotalPage}
                          onChange={(val) => {
                            setFieldValue(`states`, val);
                            setFieldValue(`city`, []);
                            // Fetch all cities for selected states (all pages) or clear if no states selected
                            if (!_.isEmpty(val)) {
                              getAllCitiesByState(val);
                            } else {
                              // Clear cities from Redux when states are cleared
                              dispatch({
                                type: GET_CITY_BY_STATE.SUCCESS,
                                payload: {
                                  results: [],
                                  page: 1,
                                  totalPages: 1,
                                  totalResults: 0,
                                },
                              });
                            }
                            // Filter out stations that don't match selected states
                            if (!_.isEmpty(val)) {
                              const selectedStateNames = Array.isArray(val) 
                                ? val.map(state => typeof state === 'string' ? state : state.value || state)
                                : [val];
                              const filteredStations = values.charging_stations?.filter((stationId) => {
                                const station = allStationByCity.find(s => s.id === stationId);
                                return station && selectedStateNames.includes(station.state);
                              }) || [];
                              setFieldValue(`charging_stations`, filteredStations);
                              if (_.isEmpty(filteredStations)) {
                                setFieldValue(`chargers`, []);
                              }
                            } else {
                              // If states are cleared, keep stations but clear chargers if no stations
                              if (_.isEmpty(values.charging_stations)) {
                                setFieldValue(`chargers`, []);
                              }
                            }
                          }}
                        />
                      </Col>
                      <Col lg={3} md={6}>
                        <Select
                          label={t('filters.city')}
                          options={
                            !_.isEmpty(values.states) && cityByState && cityByState.length > 0
                              ? _.map(cityByState, (item) => {
                                  return { label: item.name, value: item.name };
                                })
                              : []
                          }
                          placeholder={t('placeHolder.selectCity')}
                          name="city"
                          isMulti
                          value={values.city}
                          onMenuScrollDown={true}
                          getDataOnScrollDown={(data) => getCityByState({ ...data, state_names: values.states })}
                          page={cityPage}
                          totalPage={cityTotalPage}
                          onChange={(val) => {
                            setFieldValue('city', val);
                            // Clear charging stations and chargers if city changes
                            if (_.isEmpty(val)) {
                              // If city is cleared, keep stations but clear chargers if no stations
                              if (_.isEmpty(values.charging_stations)) {
                                setFieldValue(`chargers`, []);
                              }
                            } else {
                              // Filter out stations that don't match selected cities
                              const selectedCityNames = Array.isArray(val) 
                                ? val.map(city => typeof city === 'string' ? city : city.value || city)
                                : [val];
                              // Also consider state filter if states are selected
                              let availableStations = allStationByCity;
                              if (values?.states && values.states.length > 0) {
                                const selectedStateNames = Array.isArray(values.states) 
                                  ? values.states.map(state => typeof state === 'string' ? state : state.value || state)
                                  : [values.states];
                                availableStations = availableStations.filter((station) => 
                                  selectedStateNames.includes(station.state)
                                );
                              }
                              const filteredStations = values.charging_stations?.filter((stationId) => {
                                const station = availableStations.find(s => s.id === stationId);
                                return station && selectedCityNames.includes(station.city);
                              }) || [];
                              setFieldValue(`charging_stations`, filteredStations);
                              if (_.isEmpty(filteredStations)) {
                                setFieldValue(`chargers`, []);
                              }
                            }
                          }}
                        />
                      </Col>
                      <Col lg={6} md={12}>
                        <Select
                          label={t('addVehicleForm.chargingStation')}
                          options={[
                            ..._.map((() => {
                              let filteredStations = allStationByCity;

                              // Filter by user profile stations
                              if (userProfileStations && userProfileStations.length > 0) {
                                filteredStations = filteredStations.filter((x) => userProfileStations.indexOf(x.id) !== -1);
                              }

                              // Filter by selected states if any state is selected
                              if (values?.states && values.states.length > 0) {
                                const selectedStateNames = Array.isArray(values.states) 
                                  ? values.states.map(state => typeof state === 'string' ? state : state.value || state)
                                  : [values.states];
                                filteredStations = filteredStations.filter((station) => 
                                  selectedStateNames.includes(station.state)
                                );
                              }

                              // Filter by selected cities if any city is selected
                              if (values?.city && values.city.length > 0) {
                                const selectedCityNames = Array.isArray(values.city) 
                                  ? values.city.map(city => typeof city === 'string' ? city : city.value || city)
                                  : [values.city];
                                filteredStations = filteredStations.filter((station) => 
                                  selectedCityNames.includes(station.city)
                                );
                              }

                              return filteredStations;
                            })(), (station) => {
                              return { label: station.name, value: station.id };
                            }),
                          ]}
                          placeholder={t('placeHolder.selectStation')}
                          name="charging_stations"
                          isMulti
                          value={values.charging_stations}
                          onMenuScrollDown={true}
                          onChange={(val) => {
                            setFieldValue(`charging_stations`, val);
                            if (_.isEmpty(val)) {
                              setFieldValue(`chargers`, []);
                            }
                          }}
                        />
                      </Col>
                      <Col lg={6}>
                        <Select
                          label={t('Charger Id')}
                          options={
                            (() => {
                              let filteredStations = allStationByCity;

                              // Filter by user profile stations
                              if (userProfileStations && userProfileStations.length > 0) {
                                filteredStations = filteredStations.filter((x) => userProfileStations.indexOf(x.id) !== -1);
                              }

                              // Filter by selected states if any state is selected
                              if (values?.states && values.states.length > 0) {
                                const selectedStateNames = Array.isArray(values.states) 
                                  ? values.states.map(state => typeof state === 'string' ? state : state.value || state)
                                  : [values.states];
                                filteredStations = filteredStations.filter((station) => 
                                  selectedStateNames.includes(station.state)
                                );
                              }

                              // Filter by selected cities if any city is selected
                              if (values?.city && values.city.length > 0) {
                                const selectedCityNames = Array.isArray(values.city) 
                                  ? values.city.map(city => typeof city === 'string' ? city : city.value || city)
                                  : [values.city];
                                filteredStations = filteredStations.filter((station) => 
                                  selectedCityNames.includes(station.city)
                                );
                              }

                              return !_.isEmpty(filteredStations) && values.charging_stations
                                ? _.flatMap(
                                    filteredStations.filter((station) => values.charging_stations.includes(station.id)),
                                  (station) =>
                                    station.chargers && station.chargers.length > 0
                                      ? station.chargers
                                          .filter((charger) => !charger.is_deleted)
                                          .map((charger) => ({
                                            label: charger.charger_id,
                                            value: charger.id,
                                          }))
                                      : []
                                  )
                                : [];
                            })()
                          }
                          placeholder={t('Charger Id')}
                          name="chargers"
                          isMulti
                          value={values.chargers}
                          onMenuScrollDown={true}
                          onChange={(val) => {
                            setFieldValue('chargers', val);
                          }}
                        />
                      </Col>
                      <FieldArray
                        name="schedule"
                        render={(arrayHelpers) => (
                          <>
                            {_.map(values.schedule, (field, index) => (
                              <Row key={`station-timimg-${index}`}>
                                <Col lg={11} md={6}>
                                  <Row>
                                    <Col>
                                      <TextInput
                                        isRequired
                                        type="time"
                                        label={t('groupPricingForm.hourFrom')}
                                        name="from"
                                        value={field.from}
                                        onChange={(e) => setFieldValue(`schedule[${index}].from`, e.target.value)}
                                        error={
                                          getIn(errors, `schedule[${index}].from`) && getIn(touched, `schedule[${index}].from`)
                                            ? getIn(errors, `schedule[${index}].from`)
                                            : null
                                        }
                                      />
                                    </Col>
                                    <Col>
                                      <TextInput
                                        isRequired
                                        type="time"
                                        label={t('groupPricingForm.hourTo')}
                                        name="to"
                                        value={field.to}
                                        onChange={(e) => setFieldValue(`schedule[${index}].to`, e.target.value)}
                                        error={
                                          getIn(errors, `schedule[${index}].to`) && getIn(touched, `schedule[${index}].to`)
                                            ? getIn(errors, `schedule[${index}].to`)
                                            : null
                                        }
                                      />
                                    </Col>
                                    <Col>
                                      <TextInput
                                        isRequired
                                        type="number"
                                        label={t('groupPricingForm.defaultRate')}
                                        name="price_per_unit"
                                        value={parseFloat(field.price_per_unit)}
                                        onChange={(e) => setFieldValue(`schedule[${index}].price_per_unit`, parseFloat(e.target.value))}
                                        error={
                                          getIn(errors, `schedule[${index}].price_per_unit`) && getIn(touched, `schedule[${index}].price_per_unit`)
                                            ? getIn(errors, `schedule[${index}].price_per_unit`)
                                            : null
                                        }
                                      />
                                    </Col>
                                  </Row>
                                </Col>
                                <Col md={1} lg={1} sm={1} className="d-flex align-items-center">
                                  {index > 0 && (
                                    <Button className="field--btn" onClick={() => arrayHelpers.remove(index)}>
                                      <BiTrash size={26} />
                                    </Button>
                                  )}
                                </Col>
                              </Row>
                            ))}
                            <div>
                              <Button
                                className="field--btn"
                                onClick={() =>
                                  arrayHelpers.insert(values?.schedule?.length, {
                                    day: values.timing_type,
                                    from: values.from || '',
                                    to: values.to || '',
                                    price_per_unit: values.price_per_unit || '',
                                  })
                                }
                              >
                                <BiPlus size={22} className="plus--icon" /> {t('groupPricingForm.add')}
                              </Button>
                            </div>
                          </>
                        )}
                      />
                    </Row>
                    <div className="form-btn--block">
                      <Button className="pricing-form-btn cancel-btn" onClick={onClose}>
                        {t('button.cancel')}
                      </Button>
                      <Button type="submit" className="pricing-form-btn create-btn">
                        {isEdit ? 'Update' : 'Submit'}
                      </Button>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </Card>
      </div>
    </React.Fragment>
  );
};
PricingForm.propTypes = {
  onClose: PropTypes.func,
  isEdit: PropTypes.bool,
  onSuccess: PropTypes.func,
};
export default PricingForm;
